import { createFragmentContainer, graphql } from "react-relay";
import { AttributeTable, AttributeTableRow } from "~/components/shared/AttributeTable";
import { UserAvatarRelay as UserAvatar } from "~/components/shared/UserAvatar";
import JobEvent from "~/components/JobActivityTimeline/JobEvent";

type Props = {
  event: any;
  previousTimestamp: string | null | undefined;
};

function formatChangeLabel(key: string): string {
  if (key === "timeout_in_minutes") {
    return "Timeout";
  } else if (key === "priority") {
    return "Priority";
  }
  return key;
}

function formatChangeValue(key: string, value: any): string {
  if (key === "timeout_in_minutes") {
    if (value === null) {
      return `none`;
    }
    return `${value} minutes`;
  }
  return `${value}`;
}

function JobEventChanged({ event, previousTimestamp }: Props) {
  const { timestamp, actor, changedChanges } = event;

  // Parse the changes from the changedChanges JSON field
  let changes: [string, any][] = [];
  if (changedChanges) {
    try {
      const parsedChanges = JSON.parse(changedChanges);
      if (typeof parsedChanges === "object" && parsedChanges !== null) {
        changes = Object.entries(parsedChanges);
      }
    } catch (error) {
      console.error("Failed to parse changedChanges JSON:", error);
    }
  }

  return (
    <JobEvent timestamp={timestamp} previousTimestamp={previousTimestamp}>
      <JobEvent.Label>
        Job changed
        {actor.type === "USER" && actor?.node && (
          <>
            {" by "}
            <UserAvatar className="mr-1" size="MICRO" user={actor.node} suppressAltText={true} />
            {actor.node.name}
          </>
        )}
      </JobEvent.Label>
      {changes.length > 0 && (
        <JobEvent.Content>
          <AttributeTable>
            {changes.map(([key, [beforeValue, afterValue]]) => (
              <AttributeTableRow key={key} title={formatChangeLabel(key)}>
                <code className="dark-gray monospace">
                  {formatChangeValue(key, beforeValue)} → {formatChangeValue(key, afterValue)}
                </code>
              </AttributeTableRow>
            ))}
          </AttributeTable>
        </JobEvent.Content>
      )}
    </JobEvent>
  );
}

export default createFragmentContainer(JobEventChanged, {
  event: graphql`
    fragment JobEventChanged_event on JobEvent {
      ... on JobEvent {
        timestamp

        actor {
          type
          node {
            ... on User {
              name
              uuid
              ...UserAvatar_user
            }
          }
        }

        ... on JobEventChanged {
          changedChanges
        }
      }
    }
  `,
});
